import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator as CalcIcon, ShoppingCart, Square as RectIcon, Circle as CircleIcon, Triangle as TriIcon, Shapes as IrregIcon } from 'lucide-react';
import { useCart } from './CartContext';
import { Link } from 'react-router-dom';
export type Shape = 'rectangle' | 'circle' | 'triangle' | 'irregular';
export const projectTypes = [{
  id: 'driveway',
  name: 'Driveway',
  defaultDepth: 4
}, {
  id: 'patio',
  name: 'Patio Base',
  defaultDepth: 4
}, {
  id: 'walkway',
  name: 'Walkway',
  defaultDepth: 3
}, {
  id: 'parking',
  name: 'Parking Pad',
  defaultDepth: 6
}, {
  id: 'french-drain',
  name: 'French Drain',
  defaultDepth: 12
}, {
  id: 'fill',
  name: 'General Fill',
  defaultDepth: 6
}];
export const calcMaterials = [{
  id: '125-minus',
  name: '1.25" Minus',
  density: 1.4,
  priceRange: '$67'
}, {
  id: 'crushed-rock',
  name: '5/8" Minus',
  density: 1.4,
  priceRange: '$67'
}, {
  id: 'walkway-gravel',
  name: 'Walkway Gravel (3/8" Minus)',
  density: 1.4,
  priceRange: '$61'
}, {
  id: 'recycled-asphalt',
  name: 'Recycled Asphalt',
  density: 1.4,
  priceRange: '$44'
}, {
  id: 'recycled-concrete',
  name: 'Recycled Concrete',
  density: 1.4,
  priceRange: '$44'
}, {
  id: 'fill-dirt',
  name: 'Fill Dirt',
  density: 1.3,
  priceRange: '$24'
}, {
  id: 'builders-sand',
  name: 'Builders Sand',
  density: 1.5,
  priceRange: '$73'
}, {
  id: 'gravel-borrow',
  name: 'Gravel Borrow',
  density: 1.4,
  priceRange: '$48'
}, {
  id: '58-clear',
  name: '5/8" Crushed Clear',
  density: 1.35,
  priceRange: '$87'
}, {
  id: '78-drain',
  name: '7/8" Drain Rock',
  density: 1.35,
  priceRange: '$69'
}, {
  id: '125-clear',
  name: '1.25" Crushed Clear',
  density: 1.35,
  priceRange: '$76'
}, {
  id: '34-clean',
  name: '3/4" Crushed Clear',
  density: 1.35,
  priceRange: '$89'
}, {
  id: '15-drain',
  name: '1.5" Drain Rock',
  density: 1.35,
  priceRange: '$76'
}, {
  id: 'pea-gravel',
  name: 'Pea Gravel',
  density: 1.45,
  priceRange: '$60'
}, {
  id: 'topsoil',
  name: '3-Way Topsoil',
  density: 1.1,
  priceRange: '$48'
}, {
  id: '5050-mix',
  name: '50/50 Mix',
  density: 1.1,
  priceRange: '$57'
}, {
  id: 'compost',
  name: 'Compost',
  density: 0.6,
  priceRange: '$66'
}, {
  id: 'fine-bark',
  name: 'Fine Bark',
  density: 0.45,
  priceRange: '$50'
}, {
  id: 'medium-bark',
  name: 'Medium Bark',
  density: 0.45,
  priceRange: '$50'
}, {
  id: 'coarse-bark',
  name: 'Coarse Bark',
  density: 0.45,
  priceRange: '$50'
}, {
  id: 'play-chips',
  name: 'Play Chips',
  density: 0.45,
  priceRange: '$55'
}, {
  id: 'streambed-cobble',
  name: 'Streambed Cobble',
  density: 1.5,
  priceRange: '$61'
}, {
  id: 'river-rock',
  name: 'River Rock',
  density: 1.5,
  priceRange: '$84'
}, {
  id: 'lava-rock',
  name: 'Lava Rock',
  density: 0.75,
  priceRange: '$93'
}, {
  id: 'quarry-rock',
  name: '2-4" Spalls',
  density: 1.5,
  priceRange: '$85'
}, {
  id: '48-quarry',
  name: '4-8" Spalls',
  density: 1.5,
  priceRange: '$85'
}, {
  id: '2-ballast',
  name: '2" Ballast',
  density: 1.5,
  priceRange: '$72'
}, {
  id: 'large-rocks',
  name: 'Large Rocks (1-4 Man)',
  density: 1.6,
  priceRange: '$152'
}];
type MaterialCalculatorProps = {
  variant?: 'modal' | 'inline' | 'page';
  initialMaterial?: string;
  initialProjectType?: string;
  onAddToCart?: (yards: number, material: string) => void;
};
export function MaterialCalculator({
  variant = 'inline',
  initialMaterial = 'crushed-rock',
  initialProjectType = 'driveway',
  onAddToCart
}: MaterialCalculatorProps) {
  const {
    addToCart,
    openCart
  } = useCart();
  const [projectType, setProjectType] = useState(initialProjectType);
  const [material, setMaterial] = useState(calcMaterials.find(m => m.id === initialMaterial) ? initialMaterial : 'crushed-rock');
  const [shape, setShape] = useState<Shape>('rectangle');
  const [unit, setUnit] = useState<'ft' | 'm'>('ft');
  const [depthUnit, setDepthUnit] = useState<'in' | 'cm'>('in');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [diameter, setDiameter] = useState('');
  const [base, setBase] = useState('');
  const [height, setHeight] = useState('');
  const [depth, setDepth] = useState(() => {
    const pt = projectTypes.find(p => p.id === initialProjectType);
    return pt ? pt.defaultDepth.toString() : '4';
  });
  const [calcResults, setCalcResults] = useState<{
    yards: number;
    tons: number;
  } | null>(null);
  const handleProjectTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value;
    setProjectType(newType);
    const pt = projectTypes.find(p => p.id === newType);
    if (pt) {
      setDepth(pt.defaultDepth.toString());
    }
  };
  // Clear stale results when shape changes
  useEffect(() => {
    setCalcResults(null);
  }, [shape]);
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const d = parseFloat(depth);
    if (isNaN(d) || d <= 0) return;
    let areaSqFt = 0;
    const l = parseFloat(length) || 0;
    const w = parseFloat(width) || 0;
    const dia = parseFloat(diameter) || 0;
    const b = parseFloat(base) || 0;
    const h = parseFloat(height) || 0;
    const mToFt = 3.28084;
    const lFt = unit === 'm' ? l * mToFt : l;
    const wFt = unit === 'm' ? w * mToFt : w;
    const dFt = unit === 'm' ? dia * mToFt : dia;
    const bFt = unit === 'm' ? b * mToFt : b;
    const hFt = unit === 'm' ? h * mToFt : h;
    if (shape === 'rectangle' || shape === 'irregular') {
      if (isNaN(l) || isNaN(w) || l <= 0 || w <= 0) return;
      areaSqFt = lFt * wFt;
    } else if (shape === 'circle') {
      if (isNaN(dia) || dia <= 0) return;
      const radius = dFt / 2;
      areaSqFt = Math.PI * radius * radius;
    } else if (shape === 'triangle') {
      if (isNaN(b) || isNaN(h) || b <= 0 || h <= 0) return;
      areaSqFt = 0.5 * bFt * hFt;
    }
    const depthInFeet = depthUnit === 'cm' ? d / 30.48 : d / 12;
    const cubicFeet = areaSqFt * depthInFeet;
    const cubicYards = cubicFeet / 27;
    const selectedMaterial = calcMaterials.find(m => m.id === material);
    const density = selectedMaterial ? selectedMaterial.density : 1.4;
    const tons = cubicYards * density;
    setCalcResults({
      yards: Math.ceil(cubicYards * 10) / 10,
      tons: Math.ceil(tons * 10) / 10
    });
  };
  const handleAddToCartClick = () => {
    if (!calcResults) return;
    const roundedYards = Math.ceil(calcResults.yards);
    const selectedMaterial = calcMaterials.find(m => m.id === material);
    if (!selectedMaterial) return;
    if (onAddToCart) {
      onAddToCart(roundedYards, selectedMaterial.name);
    } else {
      addToCart({
        category: 'Calculated',
        product: selectedMaterial.name,
        quantity: roundedYards,
        pricePerYard: parseFloat(selectedMaterial.priceRange.replace('$', ''))
      });
      openCart();
    }
  };
  const inputClass = 'w-full px-4 py-3 bg-white dark:bg-[#1a1a1a] border border-[#e5e5e5] dark:border-[#333] rounded-xl text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all';
  const containerClass = variant === 'page' ? 'w-full' : variant === 'modal' ? 'w-full' : 'bg-[#f5f5f5] dark:bg-[#2a2a2a] rounded-2xl border border-[#e5e5e5] dark:border-white/10 shadow-xl overflow-hidden';
  const paddingClass = variant === 'page' ? '' : 'p-6 sm:p-8';
  return <div className={containerClass} data-id="element-1184">
      <div className={`grid grid-cols-1 ${variant === 'page' ? 'lg:grid-cols-3 gap-8' : 'lg:grid-cols-5'}`} data-id="element-1185">
        <div className={`${paddingClass} ${variant === 'page' ? 'lg:col-span-2 space-y-6' : 'lg:col-span-3 border-b lg:border-b-0 lg:border-r border-[#e5e5e5] dark:border-[#555555]'}`} data-id="element-1186">
          {/* Shape Tabs. Icon-led layout — each tab shows the shape
           glyph with a small label below. Reads instantly even in
           narrow embeds (Product page sidebar) where text-only tabs
           would truncate to a single letter. */}
          <div className="dark:bg-[#222] p-1.5 rounded-2xl border border-[#e5e5e5] dark:border-[#333] flex flex-nowrap gap-1 mb-6 bg-[#eeeeee]" data-id="element-1187">
            {[{
            id: 'rectangle' as Shape,
            Icon: RectIcon,
            label: 'Rectangle'
          }, {
            id: 'circle' as Shape,
            Icon: CircleIcon,
            label: 'Circle'
          }, {
            id: 'triangle' as Shape,
            Icon: TriIcon,
            label: 'Triangle'
          }, {
            id: 'irregular' as Shape,
            Icon: IrregIcon,
            label: 'Irregular'
          }].map(({
            id,
            Icon,
            label
          }) => <button key={id} type="button" onClick={() => setShape(id)} aria-label={label} className={`relative flex-1 min-w-0 py-2 px-1 rounded-xl transition-colors z-10 flex flex-col items-center justify-center gap-1 ${shape === id ? 'text-white' : 'text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-200'}`} data-id="element-1188">
                {shape === id && <motion.div layoutId="shapeTab" className="absolute inset-0 bg-[#3a3a3a] dark:bg-[#444] rounded-xl -z-10" transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-1189" />}
                <Icon className="w-5 h-5" strokeWidth={shape === id ? 2.25 : 1.75} data-id="element-1190" />
                <span className="text-[10px] font-semibold leading-none tracking-wide" data-id="element-1191">
                  {label}
                </span>
              </button>)}
          </div>

          <form onSubmit={handleCalculate} className="space-y-6" data-id="element-1192">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1193">
              <div data-id="element-1194">
                <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1195">
                  Project Type
                </label>
                <select value={projectType} onChange={handleProjectTypeChange} className={`${inputClass} bg-white`} data-id="element-1196">
                  {projectTypes.map(pt => <option key={pt.id} value={pt.id} data-id="element-1197">
                      {pt.name}
                    </option>)}
                </select>
              </div>
              <div data-id="element-1198">
                <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1199">
                  Material
                </label>
                <select value={material} onChange={e => setMaterial(e.target.value)} className={`${inputClass} bg-white`} data-id="element-1200">
                  {calcMaterials.map(m => <option key={m.id} value={m.id} data-id="element-1201">
                      {m.name}
                    </option>)}
                </select>
              </div>
            </div>

            <div className="flex justify-end" data-id="element-1202">
              <div className="flex dark:bg-[#1a1a1a] rounded-lg p-1 border border-[#e5e5e5] dark:border-[#333] bg-[#eeeeee]" data-id="element-1203">
                <button type="button" onClick={() => setUnit('ft')} className={`px-3 py-1 text-xs font-bold rounded-md ${unit === 'ft' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-1204">
                  Feet
                </button>
                <button type="button" onClick={() => setUnit('m')} className={`px-3 py-1 text-xs font-bold rounded-md ${unit === 'm' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-1205">
                  Meters
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1206">
              <AnimatePresence mode="wait" data-id="element-1207">
                {(shape === 'rectangle' || shape === 'irregular') && <motion.div key="rect" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1208">
                    <div data-id="element-1209">
                      <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1210">
                        Length ({unit})
                      </label>
                      <input type="number" value={length} onChange={e => setLength(e.target.value)} placeholder="0" className={`${inputClass} bg-white`} required min="0.1" step="any" data-id="element-1211" />
                    </div>
                    <div data-id="element-1212">
                      <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1213">
                        Width ({unit})
                      </label>
                      <input type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="0" className={`${inputClass} bg-white`} required min="0.1" step="any" data-id="element-1214" />
                    </div>
                  </motion.div>}
                {shape === 'circle' && <motion.div key="circ" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="col-span-1 sm:col-span-2" data-id="element-1215">
                    <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1216">
                      Diameter ({unit})
                    </label>
                    <input type="number" value={diameter} onChange={e => setDiameter(e.target.value)} placeholder="0" className={inputClass} required min="0.1" step="any" data-id="element-1217" />
                  </motion.div>}
                {shape === 'triangle' && <motion.div key="tri" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1218">
                    <div data-id="element-1219">
                      <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1220">
                        Base ({unit})
                      </label>
                      <input type="number" value={base} onChange={e => setBase(e.target.value)} placeholder="0" className={inputClass} required min="0.1" step="any" data-id="element-1221" />
                    </div>
                    <div data-id="element-1222">
                      <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1223">
                        Height ({unit})
                      </label>
                      <input type="number" value={height} onChange={e => setHeight(e.target.value)} placeholder="0" className={inputClass} required min="0.1" step="any" data-id="element-1224" />
                    </div>
                  </motion.div>}
              </AnimatePresence>

              <div className="col-span-1 sm:col-span-2 border-t border-[#e5e5e5] dark:border-[#333] pt-6 mt-2" data-id="element-1225">
                <div className="flex justify-between items-end mb-2" data-id="element-1226">
                  <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider" data-id="element-1227">
                    Depth
                  </label>
                  <div className="flex bg-[#eeeeee] dark:bg-[#1a1a1a] rounded-lg p-1 border border-[#e5e5e5] dark:border-[#333]" data-id="element-1228">
                    <button type="button" onClick={() => setDepthUnit('in')} className={`px-3 py-1 text-xs font-bold rounded-md ${depthUnit === 'in' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-1229">
                      Inches
                    </button>
                    <button type="button" onClick={() => setDepthUnit('cm')} className={`px-3 py-1 text-xs font-bold rounded-md ${depthUnit === 'cm' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-1230">
                      cm
                    </button>
                  </div>
                </div>
                <input type="number" value={depth} onChange={e => setDepth(e.target.value)} className={`${inputClass} bg-white`} placeholder="0" required min="0.1" step="any" data-id="element-1231" />
              </div>
            </div>

            <button type="submit" className="w-full py-4 bg-brand-orange hover:bg-brand-orangeHover text-white font-bold rounded-xl transition-colors flex items-center justify-center" data-id="element-1232">
              <CalcIcon className="w-5 h-5 mr-2" data-id="element-1233" />
              Calculate Yardage
            </button>
          </form>
        </div>

        <div className={`${paddingClass} ${variant === 'page' ? 'lg:col-span-1' : 'lg:col-span-2'} bg-[#eeeeee] dark:bg-[#303030] flex flex-col justify-center`} data-id="element-1234">
          {calcResults ? <div className="space-y-6" data-id="element-1235">
              <div data-id="element-1236">
                <p className="text-sm font-medium text-stone-500 dark:text-stone-400 mb-1" data-id="element-1237">
                  Estimated Volume
                </p>
                <div className="flex items-baseline gap-2" data-id="element-1238">
                  <span className="font-heading font-bold text-4xl text-stone-900 dark:text-white" data-id="element-1239">
                    {calcResults.yards}
                  </span>
                  <span className="font-medium text-stone-500 dark:text-stone-400" data-id="element-1240">
                    Cubic Yards
                  </span>
                </div>
              </div>
              <div className="pt-4 border-t border-[#e5e5e5] dark:border-[#4b4b4b]" data-id="element-1241">
                <p className="text-sm font-medium text-stone-500 dark:text-stone-400 mb-1" data-id="element-1242">
                  Estimated Weight
                </p>
                <div className="flex items-baseline gap-2" data-id="element-1243">
                  <span className="font-heading font-bold text-3xl text-stone-900 dark:text-white" data-id="element-1244">
                    {calcResults.tons}
                  </span>
                  <span className="font-medium text-stone-500 dark:text-stone-400" data-id="element-1245">
                    Tons
                  </span>
                </div>
              </div>
              <div className="pt-4 border-t border-[#e5e5e5] dark:border-[#4b4b4b]" data-id="element-1246">
                <p className="text-sm text-stone-500 dark:text-stone-400 font-medium mb-1" data-id="element-1247">
                  Material Price Range
                </p>
                <p className="text-lg font-medium text-stone-900 dark:text-white" data-id="element-1248">
                  {calcMaterials.find(m => m.id === material)?.priceRange}{' '}
                  <span className="text-sm text-stone-500 font-normal" data-id="element-1249">
                    / yard
                  </span>
                </p>
              </div>

              {variant === 'page' ? <Link to="/#materials" className="w-full py-4 rounded-xl font-bold transition-all flex items-center justify-center bg-brand-orange hover:bg-[#e66a2c] text-white shadow-lg shadow-brand-orange/20 mt-6" data-id="element-1250">
                  <ShoppingCart className="w-5 h-5 mr-2" data-id="element-1251" />
                  Shop Materials
                </Link> : <button type="button" onClick={handleAddToCartClick} className="w-full mt-6 py-3.5 bg-brand-charcoal dark:bg-white text-white dark:text-stone-900 font-bold rounded-xl hover:bg-stone-800 dark:hover:bg-stone-100 transition-colors flex items-center justify-center" data-id="element-1252">
                  <ShoppingCart className="w-4 h-4 mr-2" data-id="element-1253" />
                  {onAddToCart ? `Apply ${Math.ceil(calcResults.yards)} Yards` : `Add ${Math.ceil(calcResults.yards)} Yards to Cart`}
                </button>}
            </div> : <div className="text-center text-stone-500 dark:text-stone-400" data-id="element-1254">
              <CalcIcon className="w-12 h-12 mx-auto mb-4 opacity-20" data-id="element-1255" />
              <p data-id="element-1256">Enter your dimensions to see estimated volume and weight.</p>
            </div>}
        </div>
      </div>
    </div>;
}